import React from 'react';
import '../../styles/components.css';

export interface CardProps {
  /**
   * Card variant
   * @default 'default'
   */
  variant?: 'default' | 'interactive' | 'feature' | 'glass' | 'neumorphic';
  
  /**
   * Card content
   */
  children: React.ReactNode;
  
  /**
   * Optional card title
   */
  title?: React.ReactNode;
  
  /**
   * Optional card footer
   */
  footer?: React.ReactNode;
  
  /**
   * Optional icon to display at the top of a feature card
   */
  icon?: React.ReactNode;
  
  /**
   * Callback for when an interactive card is clicked
   */
  onClick?: () => void;
  
  /**
   * Additional CSS class names
   */
  className?: string;
  
  /**
   * Additional props to pass to the div element
   */
  [key: string]: any;
}

/**
 * Card component for content containers
 */
export const Card = ({
  variant = 'default',
  children,
  title,
  footer,
  icon,
  onClick,
  className = '',
  ...props
}: CardProps) => {
  // Compute class names
  const baseClass = 'card';
  const variantClass = variant !== 'default' ? `card-${variant}` : '';
  
  const classes = [
    baseClass,
    variantClass,
    className
  ].filter(Boolean).join(' ');

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (variant === 'interactive' && onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div 
      className={classes}
      onClick={variant === 'interactive' ? onClick : undefined}
      onKeyDown={handleKeyDown}
      tabIndex={variant === 'interactive' ? 0 : undefined}
      role={variant === 'interactive' ? 'button' : undefined}
      {...props}
    >
      {variant === 'feature' && icon && (
        <div className="mb-4 text-primary">
          {icon}
        </div>
      )}
      
      {title && (
        <div className="mb-4 font-semibold text-xl">{title}</div>
      )}
      
      <div className="card-body">
        {children}
      </div>
      
      {footer && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          {footer}
        </div>
      )}
    </div>
  );
};

export default Card;

// Card subcomponents
export interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <div className={`card-header ${className}`} {...props}>
    {children}
  </div>
);

export interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}

export const CardTitle: React.FC<CardTitleProps> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <h3 className={`card-title ${className}`} {...props}>
    {children}
  </h3>
);

export interface CardDescriptionProps {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}

export const CardDescription: React.FC<CardDescriptionProps> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <p className={`card-description ${className}`} {...props}>
    {children}
  </p>
);

export interface CardContentProps {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}

export const CardContent: React.FC<CardContentProps> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <div className={`card-content ${className}`} {...props}>
    {children}
  </div>
);

export interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}

export const CardFooter: React.FC<CardFooterProps> = ({ 
  children, 
  className = '', 
  ...props 
}) => (
  <div className={`card-footer ${className}`} {...props}>
    {children}
  </div>
);
